// components/layout/PageContainer/PageContainer.tsx
import { ReactNode, ReactElement, ElementType } from 'react';
import { Box, Typography, Container, BoxProps, SvgIconProps } from '@mui/material';
import { SxProps, Theme, Breakpoint } from '@mui/material/styles';
import { NavMenuV2 } from '../NavMenu/NavMenuV2';
import { MobileQuickActions } from '../../../features/landing/components/MobileQuickActions';
import type { UserData } from '../../../context/AuthContext';

type GradientColor = 'primary' | 'secondary' | 'warning' | 'danger' | 'success';

// Gradient color mappings
const gradients: Record<GradientColor, string> = {
  primary: 'linear-gradient(90deg, #00FFD4, #00D4FF)',
  secondary: 'linear-gradient(90deg, #7B68EE, #00FFD4)',
  warning: 'linear-gradient(90deg, #FFE600, #FF6B35)',
  danger: 'linear-gradient(90deg, #FF3366, #FF6B35)',
  success: 'linear-gradient(90deg, #00FF88, #00FFD4)',
};

export interface PageContainerProps extends Omit<BoxProps, 'title' | 'maxWidth'> {
  children: ReactNode;
  user?: UserData | null;
  title?: string;
  subtitle?: string;
  titleGradient?: boolean;
  gradientColors?: GradientColor;
  maxWidth?: Breakpoint | false;
  centered?: boolean;
  action?: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * PageContainer - Standard page wrapper with navigation
 */
export function PageContainer({
  children,
  user,
  title,
  subtitle,
  titleGradient = true,
  gradientColors = 'primary',
  maxWidth = 'xl',
  centered = true,
  action,
  sx = {},
  ...props
}: PageContainerProps): ReactElement {
  return (
    <Box
      sx={{
        backgroundColor: '#0A0E14',
        backgroundImage: 'radial-gradient(ellipse at top, #151B23 0%, #0A0E14 70%)',
        minHeight: '100vh',
        color: '#FFFFFF',
        ...sx,
      }}
      {...props}
    >
      {/* Navigation */}
      {user && <NavMenuV2 user={user} />}

      {/* Main Content - adjusted padding for fixed top bar and mobile bottom nav */}
      <Container
        maxWidth={maxWidth}
        sx={{
          pt: user ? '88px' : 4, // 64px for navbar + 24px spacing
          pb: { xs: 12, sm: 4 }, // Extra padding on mobile for bottom nav
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Page Header */}
        {title && (
          <Box
            sx={{
              mb: 4,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: centered ? 'center' : 'flex-start',
              justifyContent: centered ? 'center' : 'space-between',
              textAlign: centered ? 'center' : 'left',
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontFamily: '"Rajdhani", sans-serif',
                  fontWeight: 700,
                  animation: 'fade-in 0.5s ease-out',
                  ...(titleGradient ? {
                    background: gradients[gradientColors] || gradients.primary,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  } : {
                    color: '#FFFFFF',
                  }),
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#8892A0',
                    mt: 0.5,
                    animation: 'fade-in 0.5s ease-out 0.1s both',
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
            {action && (
              <Box sx={{ animation: 'fade-in 0.5s ease-out 0.2s both' }}>
                {action}
              </Box>
            )}
          </Box>
        )}

        {/* Page Content */}
        <Box
          sx={{
            animation: 'slide-up 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          }}
        >
          {children}
        </Box>
      </Container>

      {/* Mobile Quick Actions - Fixed bottom navigation */}
      {user && <MobileQuickActions user={user} />}
    </Box>
  );
}

export interface PageSectionProps extends BoxProps {
  title?: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * PageSection - Styled section within a page
 */
export function PageSection({ title, children, sx = {}, ...props }: PageSectionProps): ReactElement {
  return (
    <Box sx={{ mb: 4, ...sx }} {...props}>
      {title && (
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 600,
            color: '#00FFD4',
            mb: 2,
            pb: 1,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {title}
        </Typography>
      )}
      {children}
    </Box>
  );
}

export interface EmptyStateProps extends BoxProps {
  icon?: ElementType<SvgIconProps>;
  title?: string;
  description?: string;
  action?: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * EmptyState - Display when no data is available
 */
export function EmptyState({
  icon: Icon,
  title = 'No data',
  description,
  action,
  sx = {},
  ...props
}: EmptyStateProps): ReactElement {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 8,
        px: 2,
        ...sx,
      }}
      {...props}
    >
      {Icon && (
        <Icon
          sx={{
            fontSize: 64,
            color: '#4A5568',
            mb: 2,
            opacity: 0.5,
          }}
        />
      )}
      <Typography
        variant="h6"
        sx={{
          fontFamily: '"Rajdhani", sans-serif',
          color: '#8892A0',
          mb: 1,
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          sx={{ color: '#4A5568', mb: 3 }}
        >
          {description}
        </Typography>
      )}
      {action}
    </Box>
  );
}

export default PageContainer;
